import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  describeApiError,
  type EmbeddingsProvider,
  type EmbeddingsTestResult,
} from "@/lib/embeddingsProviderLogic";

/** Error carrying the backend's machine-readable `code` (test_required, not_tested, ...). */
export class EmbeddingsApiError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = "EmbeddingsApiError";
    this.code = code;
  }
}

async function call<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("llm-providers", { body: { ...body, kind: "embeddings" } });
  if (error) {
    // Non-2xx: the JSON body ({error, code}) is on error.context (a Response).
    let code: string | undefined;
    let message: string | undefined;
    try {
      const ctx = (error as { context?: Response }).context;
      const parsed = ctx && typeof ctx.json === "function" ? await ctx.json() : null;
      if (parsed && typeof parsed === "object") {
        code = typeof parsed.code === "string" ? parsed.code : undefined;
        message = typeof parsed.error === "string" ? parsed.error : undefined;
      }
    } catch { /* body not JSON */ }
    throw new EmbeddingsApiError(describeApiError(code, message ?? error.message), code);
  }
  if (data && typeof data === "object" && "error" in data && data.error) {
    const d = data as { error: unknown; code?: unknown };
    const code = typeof d.code === "string" ? d.code : undefined;
    throw new EmbeddingsApiError(describeApiError(code, String(d.error)), code);
  }
  return data as T;
}

export interface EmbeddingsSaveInput {
  id?: string;
  label: string;
  preset: string;
  baseUrl: string;
  apiKey?: string;
  defaultModel: string;
  testToken: string;
  /** Native dimension from the passing test (bound into the token server-side). */
  dimension: number;
}

export function useEmbeddingsProviders() {
  const [providers, setProviders] = useState<EmbeddingsProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (opts?: { quiet?: boolean }) => {
    if (!opts?.quiet) setLoading(true);
    setError(null);
    try {
      const { providers } = await call<{ providers: EmbeddingsProvider[] }>({ action: "list" });
      setProviders(providers);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load embeddings providers");
    } finally {
      if (!opts?.quiet) setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const testProvider = useCallback(
    (input: { id?: string; baseUrl: string; apiKey?: string; defaultModel: string }) =>
      call<EmbeddingsTestResult>({ action: "test", ...input }),
    [],
  );

  const saveProvider = useCallback(
    async (input: EmbeddingsSaveInput) => {
      const r = await call<{ provider: EmbeddingsProvider; reembedRequired: boolean }>({ action: "save", ...input });
      await refresh({ quiet: true });
      return r;
    },
    [refresh],
  );

  const setActive = useCallback(
    async (id: string) => { await call({ action: "set_active", id }); await refresh({ quiet: true }); },
    [refresh],
  );

  const deactivate = useCallback(
    async (id: string) => { await call({ action: "deactivate", id }); await refresh({ quiet: true }); },
    [refresh],
  );

  const deleteProvider = useCallback(
    async (id: string) => { await call({ action: "delete", id }); await refresh({ quiet: true }); },
    [refresh],
  );

  return { providers, loading, error, refresh, testProvider, saveProvider, setActive, deactivate, deleteProvider };
}
